import { useCallback, useMemo } from 'react';

import { filterNonExpiredTickets } from '#utils.js';
import {
  EventDate,
  EventDatesResponse,
  useListEventDates,
  useShowCampaignById_new,
} from '@givebacks/api/store';
import { EndedCampaignAlert, ProgressData } from 'core/Components';
import {
  ButtonMantine,
  NoDataMessageCard,
  SplitLayoutCard,
  Text,
  ThemeIcon,
  centsToDollar,
} from 'ui';
import { IconTicket } from 'ui/Icons';

type GetTicketsCardProps = {
  causeId?: string;
  eventId?: string;
  setIsGetTicketsModalOpen: (isOpen: boolean) => void;
  isAllTicketsExpired?: boolean;
};

const GetTicketsCard = ({
  setIsGetTicketsModalOpen,
  eventId,
  causeId,
  isAllTicketsExpired,
}: GetTicketsCardProps) => {
  const { data: event } = useShowCampaignById_new({
    causeId,
    campaignId: eventId,
  });
  const { data: eventDates } = useListEventDates({
    eventId,
    causeId,
    include: 'event_date_tickets',
    select: useCallback((data: EventDatesResponse<EventDate[]>) => data.event_dates, []),
  });

  const nonExpiredTicketTypes = useMemo(() => {
    const nonExpiredTickets = eventDates?.flatMap(
      (eventDate) => eventDate?.event_date_tickets?.map((ticket) => ticket.store_item) ?? []
    );
    return nonExpiredTickets?.filter(filterNonExpiredTickets);
  }, [eventDates]);

  // Calculate the minimum and maximum ticket prices from the non-expired ticket types
  const { minTicketPrice, maxTicketPrice } = useMemo(() => {
    // If there are no non-expired ticket types, return null for both min and max ticket prices
    if (!nonExpiredTicketTypes) return { minTicketPrice: null, maxTicketPrice: null };

    // Reduce the non-expired ticket types to find the minimum and maximum ticket prices
    return nonExpiredTicketTypes.reduce(
      (acc, ticket) => {
        // Update the minimum ticket price if the current ticket price is lower
        if (ticket.price < acc.minTicketPrice) acc.minTicketPrice = ticket.price;
        // Update the maximum ticket price if the current ticket price is higher
        if (ticket.price > acc.maxTicketPrice) acc.maxTicketPrice = ticket.price;
        return acc;
      },
      {
        // Initialize min and max ticket prices with the price of the first non-expired ticket
        minTicketPrice: nonExpiredTicketTypes[0]?.price,
        maxTicketPrice: nonExpiredTicketTypes[0]?.price,
      }
    );
  }, [nonExpiredTicketTypes]);

  const ticketPriceRange = useMemo(() => {
    if (minTicketPrice == null || maxTicketPrice == null) {
      return null;
    }

    if (minTicketPrice === 0 && maxTicketPrice === 0) {
      return 'Free';
    }

    if (minTicketPrice === maxTicketPrice) {
      return `${centsToDollar(minTicketPrice)}`;
    }

    return `${centsToDollar(minTicketPrice)} - ${centsToDollar(maxTicketPrice)}`;
  }, [minTicketPrice, maxTicketPrice]);

  const topComponent = useMemo(
    () =>
      event?.show_goal || event?.show_amount_raised ? (
        <ProgressData
          size={'lg'}
          goal={event?.goal}
          amount_raised={event?.amount_raised ?? 0}
          showAmountRaised={event?.show_amount_raised}
        />
      ) : minTicketPrice != null && maxTicketPrice != null ? (
        <Text ta="center" c="gray">
          Ticket Price:{' '}
          <Text span c="gray.8" fw="600">
            {ticketPriceRange}
          </Text>
        </Text>
      ) : null,
    [
      event?.show_goal,
      event?.show_amount_raised,
      event?.goal,
      event?.amount_raised,
      minTicketPrice,
      maxTicketPrice,
      ticketPriceRange,
    ]
  );

  const bottomComponent = useMemo(() => {
    if (!event) return null;

    if (event.status === 'finished') {
      return <EndedCampaignAlert type="event" />;
    }
    if (isAllTicketsExpired) {
      return (
        <NoDataMessageCard
          leftSection={
            <ThemeIcon variant="transparent" c="gray.4" size={18} mr={-8}>
              <IconTicket />
            </ThemeIcon>
          }
          title="Ticket Sales Closed"
          message="Ticket sales for this event are now closed. We appreciate your interest and hope you'll join us for future events."
        />
      );
    }
    return (
      <ButtonMantine onClick={() => setIsGetTicketsModalOpen(true)}>Get Tickets</ButtonMantine>
    );
  }, [event, isAllTicketsExpired, setIsGetTicketsModalOpen]);

  return (
    <SplitLayoutCard
      topComponent={topComponent}
      bottomComponent={bottomComponent}
      hideDivider={isAllTicketsExpired}
    />
  );
};

export { GetTicketsCard };
